// screen: EPG — Guide · Versions · Recurring · Import · Export · Publishing
// Programme guide per channel: day/week guide (published or draft) + edit drawer with rich metadata and rights,
// versions + rollback, recurring slots, XMLTV upload + scheduled source, public links, partner destinations.
// Studio changes epg-section, epg-publishing; API: control-plane changes epg, epg-publishing.
import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { Ban, CalendarDays, ChevronLeft, ChevronRight, Clock, Copy, Plus, Upload, RefreshCw, Trash2, PlayCircle } from 'lucide-react';
import { ApiError, type EPGIssue, type EPGMeta, type Programme } from '@viewstream/api-client';
import {
  Badge, Button, ConfirmByNameDialog, EmptyState, ErrorState, Field, Input, Select, Sheet, SheetContent, Skeleton, Switch,
  Tabs, TabsContent, TabsList, TabsTrigger, cn, useToast,
} from '@viewstream/ui';
import { fmtDate, fmtDateTime, fmtTime } from '@viewstream/i18n';
import { appTitle } from '~/app/brand';
import { BlackoutDialog } from '~/app/Security';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { can } from '~/app/permissions';
import { useChannels, useMarker, useNow, useProgrammes, type Channel } from '~/app/live';
import { dayStart, fromLocalInput, layoutColumn, nowLine, REDGE_PRESETS, toLocalInput, validateConfig, type Block, type EpgConfig } from '~/app/epg';
import { Thumb } from '~/app/Thumb';
import { firstFrameCandidates, recCdn } from '~/app/thumbs';
import { useChannelEpg, useDeleteProgramme, useImportNow, useSaveEpg, useSaveProgramme, useUploadXmltv, type EpgImportResult, type SaveResult } from '~/app/epgHooks';
import { useEpgDraft, useEpgValidate, useEpgWorkflow, useStageOps } from '~/app/epgPubHooks';
import { draftProgrammes, formMeta, issuesByProgramme, metaForm, rightsOff, type MetaForm, type RowStatus } from '~/app/epgpub';
import {
  CopyDialog, PublishingPanel, RichMetaFields, ShiftDialog, TemplatesPanel, VersionsPanel, WorkflowBar, canWriteEpg, useLockGuard, type GuideLayer,
} from '~/app/EpgPublishing';
import { FixPanel, IssueMarker, IssuesPanel, IssuesToggle, issueField, type FixField } from '~/app/EpgWarnings';
import { SubtitleLanguages } from '~/app/Subtitles';
import { ContentSummaryPanel } from '~/app/ContentSummary';
import { AIEpgPanel } from '~/app/AIEpg';
import { ArticleRequestPanel } from '~/app/AIArticle';
import { VerticalClipsPanel } from '~/app/VerticalClips';
import { ExclusionBadge, ProgrammeExclusionAction } from '~/app/CatchupExclusions';
import { exclusionOf, isExcluded } from '~/app/catchupExclusions';

type Tab = 'guide' | 'versions' | 'recurring' | 'import' | 'export' | 'publishing' | 'ai';
type View = 'day' | 'week';
const TABS: Tab[] = ['guide', 'versions', 'recurring', 'import', 'export', 'publishing', 'ai'];

interface Search { channel?: string; tab?: Tab; view?: View; day?: string; layer?: GuideLayer }

export const Route = createFileRoute('/_layout/t/$slug/epg')({
  component: Screen,
  validateSearch: (s: Record<string, unknown>): Search => ({
    channel: typeof s['channel'] === 'string' ? s['channel'] : undefined,
    tab: TABS.includes(s['tab'] as Tab) ? (s['tab'] as Tab) : undefined,
    view: s['view'] === 'week' ? 'week' : s['view'] === 'day' ? 'day' : undefined,
    day: typeof s['day'] === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s['day']) ? s['day'] : undefined,
    layer: s['layer'] === 'draft' ? 'draft' : undefined,
  }),
});

function Screen() {
  const { t } = useLingui();
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  React.useEffect(() => { document.title = appTitle(t`EPG`); }, [t]);
  const chq = useChannels(tenant.slug);
  const set = (patch: Partial<Search>) => void navigate({ to: '/t/$slug/epg', params: { slug }, search: { ...search, ...patch }, replace: true } as never);

  if (chq.isPending) return <Skeleton className="h-64 w-full" />;
  if (chq.isError) return <ErrorState error={chq.error as ApiError} onRetry={() => chq.refetch()} labels={{ title: t`Could not load channels`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  const channels = chq.data;
  if (channels.length === 0) return <EmptyState icon={<CalendarDays className="size-8" aria-hidden />} title={t`No channels yet`} description={t`The programme guide belongs to a live channel.`} />;
  const ch = channels.find((c) => c.id === search.channel) ?? channels[0]!;
  const tab = search.tab ?? 'guide';

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t`EPG`}</h1>
        <div className="flex items-center gap-2">
          {channels.length > 1 ? (
            <Select ariaLabel={t`Channel`} value={ch.id} onValueChange={(v) => set({ channel: v })} options={channels.map((c) => ({ value: c.id!, label: c.title ?? c.slug! }))} className="w-56" />
          ) : <span className="text-sm text-fg-muted">{ch.title ?? ch.slug}</span>}
          <Button variant="secondary" size="sm" onClick={() => void navigate({ to: '/t/$slug/live/$channelId', params: { slug, channelId: ch.id! }, search: { tab: 'programmes' } } as never)}>{t`Open channel`}</Button>
        </div>
      </div>
      <Tabs value={tab} onValueChange={(v) => set({ tab: v as Tab })}>
        <TabsList>
          <TabsTrigger value="guide">{t`Guide`}</TabsTrigger>
          <TabsTrigger value="versions">{t`Versions`}</TabsTrigger>
          <TabsTrigger value="recurring">{t`Recurring slots`}</TabsTrigger>
          <TabsTrigger value="import">{t`Import`}</TabsTrigger>
          <TabsTrigger value="export">{t`Export`}</TabsTrigger>
          <TabsTrigger value="publishing">{t`Publishing`}</TabsTrigger>
          <TabsTrigger value="ai">{t`AI guide`}</TabsTrigger>
        </TabsList>
        <TabsContent value="guide" className="pt-4"><Guide ch={ch} view={search.view ?? 'day'} day={search.day} layer={search.layer ?? 'published'} onNav={set} /></TabsContent>
        <TabsContent value="versions" className="pt-4">{tab === 'versions' ? <VersionsPanel ch={ch} /> : null}</TabsContent>
        <TabsContent value="recurring" className="pt-4">{tab === 'recurring' ? <TemplatesPanel ch={ch} /> : null}</TabsContent>
        <TabsContent value="import" className="pt-4"><ImportPanel ch={ch} /></TabsContent>
        <TabsContent value="export" className="pt-4"><ExportPanel ch={ch} /></TabsContent>
        <TabsContent value="publishing" className="pt-4">{tab === 'publishing' ? <PublishingPanel ch={ch} /> : null}</TabsContent>
        <TabsContent value="ai" className="pt-4">{tab === 'ai' ? <AIEpgPanel channelId={ch.id!} /> : null}</TabsContent>
      </Tabs>
    </div>
  );
}

// --- Guide -------------------------------------------------------------------------------------------------------

function Guide({ ch, view, day, layer, onNav }: { ch: Channel; view: View; day?: string; layer: GuideLayer; onNav: (p: Partial<Search>) => void }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale, tz } = useLocale();
  const now = useNow(30_000);
  const anchor = day ? Date.parse(fromLocalInput(`${day}T12:00`, tz)!) : now;
  const from = dayStart(anchor, tz, view === 'week' ? -((new Date(dayStart(anchor, tz)).getUTCDay() + 7) % 7) : 0);
  const days = view === 'week' ? 7 : 1;
  const to = dayStart(from + 12 * 3600_000, tz, days);
  const pq = useProgrammes(tenant.slug, ch.id!, from, to);
  // change epg-publishing: the draft layer (the published guide with the staged changes, flagged)
  const wf = useEpgWorkflow(tenant.slug, ch.id);
  const dq = useEpgDraft(tenant.slug, ch.id!, from, to, layer === 'draft' && !!wf.data);
  const draft = React.useMemo(() => draftProgrammes(dq.data), [dq.data]);
  const vq = useEpgValidate(tenant.slug, ch.id!, from, to, false, layer === 'published' && !!wf.data);
  const draftLayer = layer === 'draft' && !!wf.data;
  const q = draftLayer ? { isPending: dq.isPending, isError: dq.isError, error: dq.error as unknown, refetch: () => void dq.refetch(), data: draft.ps }
    : { isPending: pq.isPending, isError: pq.isError, error: pq.error as unknown, refetch: () => void pq.refetch(), data: pq.data ?? [] };
  const issues = issuesByProgramme(layer === 'draft' ? dq.data?.issues : vq.data);
  const allIssues = layer === 'draft' ? (dq.data?.issues ?? []) : (vq.data ?? []);
  const [showIssues, setShowIssues] = React.useState(false);
  const [tool, setTool] = React.useState<'copy' | 'shift' | null>(null);
  const canWrite = canWriteEpg(scopes);
  const [edit, setEdit] = React.useState<EditState | null>(null);
  const titles = React.useMemo(() => new Map(q.data.map((p) => [p.id!, p.title ?? ''])), [q.data]);
  // change epg-warnings: "Fix" opens the programme's drawer on the field that fixes the issue
  const onFix = (i: EPGIssue) => {
    const p = q.data.find((x) => x.id === i.programme_id);
    if (p) setEdit({ id: p.id, p, focus: issueField(i) });
  };
  const marker = useMarker(tenant.slug, ch.id!);
  const toast = useToast();
  const fmtDay = (ms: number) => fmtDate(new Date(ms), { locale, tz });
  const move = (n: number) => onNav({ day: toLocalInput(dayStart(from + 12 * 3600_000, tz, n * days), tz).slice(0, 10) });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-border" role="group" aria-label={t`View`}>
          <Button size="sm" variant={view === 'day' ? 'primary' : 'ghost'} aria-pressed={view === 'day'} onClick={() => onNav({ view: 'day' })}>{t`Day`}</Button>
          <Button size="sm" variant={view === 'week' ? 'primary' : 'ghost'} aria-pressed={view === 'week'} onClick={() => onNav({ view: 'week' })}>{t`Week`}</Button>
        </div>
        <Button size="icon" variant="ghost" aria-label={t`Previous`} onClick={() => move(-1)}><ChevronLeft className="size-4 icon-dir" aria-hidden /></Button>
        <Button size="sm" variant="secondary" onClick={() => onNav({ day: undefined })}>{t`Today`}</Button>
        <Button size="icon" variant="ghost" aria-label={t`Next`} onClick={() => move(1)}><ChevronRight className="size-4 icon-dir" aria-hidden /></Button>
        <span className="text-sm font-medium" data-testid="epg-range">{view === 'week' ? `${fmtDay(from)} – ${fmtDay(to - 1)}` : fmtDay(from)}</span>
        {allIssues.length ? <IssuesToggle issues={allIssues} open={showIssues} onToggle={() => setShowIssues(!showIssues)} /> : null}
        <span className="flex-1" />
        {canWrite && wf.data ? (
          <>
            <Button size="sm" variant="ghost" onClick={() => setTool('copy')}><Copy className="size-4" aria-hidden /> {t`Copy…`}</Button>
            <Button size="sm" variant="ghost" onClick={() => setTool('shift')}><Clock className="size-4" aria-hidden /> {t`Move…`}</Button>
          </>
        ) : null}
        {canWrite ? (
          <>
            <Button size="sm" variant="secondary" loading={marker.isPending} onClick={() => marker.mutate(t`Programme`, {
              onSuccess: () => toast.push({ title: t`New programme started`, tone: 'ok' }),
              onError: (err) => toast.push({ title: t`Could not start a programme`, description: err instanceof ApiError ? (err.problem?.detail ?? err.message) : String(err), tone: 'danger' }),
            })}><PlayCircle className="size-4" aria-hidden /> {t`Start next programme now`}</Button>
            <Button size="sm" onClick={() => setEdit({ start: Math.max(from, Math.ceil(now / 900_000) * 900_000) })}><Plus className="size-4" aria-hidden /> {t`Add programme`}</Button>
          </>
        ) : null}
      </div>

      {wf.data ? <WorkflowBar ch={ch} layer={layer} onLayer={(l) => onNav({ layer: l === 'draft' ? 'draft' : undefined })} from={from} to={to} /> : null}
      {showIssues && allIssues.length ? <IssuesPanel issues={allIssues} titles={titles} multiDay={view === 'week'} onFix={onFix} canFix /> : null}

      {q.isPending ? <Skeleton className="h-96 w-full" /> : q.isError ? (
        <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load programmes`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />
      ) : q.data.length === 0 ? (
        <EmptyState icon={<CalendarDays className="size-8" aria-hidden />} title={t`No programmes in this period`}
          description={t`Import an XMLTV file or set a scheduled source in the Import tab.`} />
      ) : view === 'day' ? (
        <DayList ch={ch} ps={q.data} from={from} to={to} now={now} status={layer === 'draft' ? draft.status : undefined} issues={issues} onOpen={(b, p) => setEdit({ id: b.id, p })} onFix={onFix} />
      ) : (
        <WeekGrid ps={q.data} from={from} now={now} status={layer === 'draft' ? draft.status : undefined} onOpen={(b, p) => setEdit({ id: b.id, p })} />
      )}

      <ProgrammeDrawer ch={ch} state={edit} onClose={() => setEdit(null)} readOnly={!canWrite || (layer === 'draft' && draft.status.get(edit?.id ?? '') === 'removed')}
        lockHours={wf.data?.lock_hours} review={wf.data?.mode === 'review'} draftLayer={draftLayer} issues={edit?.id ? issues.get(edit.id) : undefined} />
      {canWrite && wf.data ? (
        <>
          <CopyDialog ch={ch} open={tool === 'copy'} onOpenChange={(o) => setTool(o ? 'copy' : null)} anchor={from} hours={wf.data.lock_hours} />
          <ShiftDialog ch={ch} open={tool === 'shift'} onOpenChange={(o) => setTool(o ? 'shift' : null)} anchor={Math.max(now, from)} hours={wf.data.lock_hours} />
        </>
      ) : null}
    </div>
  );
}

function StatusBadge({ status }: { status?: RowStatus }) {
  const { t } = useLingui();
  if (!status) return null;
  return status === 'added' ? <Badge tone="ok">{t`New`}</Badge> : status === 'changed' ? <Badge tone="warn">{t`Changed`}</Badge> : <Badge tone="danger">{t`Removed`}</Badge>;
}

function SourceBadge({ source }: { source: string }) {
  const { t } = useLingui();
  return source === 'epg' ? <Badge tone="neutral">{t`EPG`}</Badge> : source === 'manual' ? <Badge tone="primary">{t`Manual`}</Badge> : <Badge tone="neutral">{source}</Badge>;
}

/** Day view: a vertical list with a time column, now-line and on-air highlight (readable at any zoom, RTL-safe). */
function DayList({ ps, from, to, now, onOpen, onFix, ch, status, issues }: { ps: Programme[]; from: number; to: number; now: number; onOpen: (b: Block, p: Programme) => void; onFix: (i: EPGIssue) => void; ch: Channel; status?: Map<string, RowStatus>; issues?: Map<string, EPGIssue[]> }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale, tz } = useLocale();
  const blocks = layoutColumn(ps, from, to, now);
  const byId = new Map(ps.map((p) => [p.id!, p]));
  const line = nowLine(from, to, now);
  return (
    <ol className="divide-y divide-border rounded-lg border border-border bg-surface" aria-label={t`Programmes`} data-testid="epg-day">
      {blocks.map((b) => (
        <li key={b.id} className={cn('relative', b.onAir && 'bg-primary/5', status?.get(b.id) === 'removed' && 'opacity-60')} data-status={status?.get(b.id)}>
          {b.onAir && line !== null ? <span className="absolute inset-y-0 start-0 w-1 bg-live" aria-hidden /> : null}
          <button type="button" onClick={() => onOpen(b, byId.get(b.id)!)} className="flex w-full items-start gap-4 px-4 py-2.5 text-start hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-primary">
            <span className="w-28 shrink-0 text-sm tabular-nums ltr-isolate text-fg-muted">
              {fmtTime(new Date(b.startMs), { locale, tz })}–{fmtTime(new Date(b.endMs), { locale, tz })}
            </span>
            <span className={cn('min-w-0 flex-1', issues?.get(b.id)?.length && 'pe-10 sm:pe-36')}>
              <span className="flex items-center gap-2">
                <span className={cn('truncate font-medium', status?.get(b.id) === 'removed' && 'line-through')}>{b.title}</span>
                {b.onAir ? <Badge tone="live">{t`On air`}</Badge> : null}
                <SourceBadge source={b.source} />
                <StatusBadge status={status?.get(b.id)} />
                {rightsOff(b.meta as never).includes('catchup') ? <Badge tone="warn">{t`No catch-up`}</Badge> : rightsOff(b.meta as never).includes('start_over') ? <Badge tone="warn">{t`No start-over`}</Badge> : null}
                <ExclusionBadge s={exclusionOf(byId.get(b.id))} />
              </span>
              {b.meta.description ? <span className="line-clamp-1 text-sm text-fg-muted">{b.meta.description}</span> : null}
            </span>
            {/* first frame of the programme from the recorder's snapshots; the EPG source's own image otherwise */}
            <Thumb candidates={[...firstFrameCandidates(recCdn(ch as never, tenant.cdn_hostname), tenant.slug, ch.slug!, b.startMs, now), ...(b.meta.image ? [b.meta.image] : [])]}
              className="hidden h-12 w-20 shrink-0 rounded bg-surface-2 object-cover sm:block" />
          </button>
          {/* change epg-warnings: a separate control (not inside the row button) with a real tooltip and a fix action */}
          {/* placed over the row's free space between the title and the thumbnail (the title reserves room for it) */}
          {issues?.get(b.id)?.length ? (
            <span className="absolute end-3 top-2 sm:end-[7.75rem]"><IssueMarker issues={issues.get(b.id)!} onFix={onFix} /></span>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

/** Week view: seven day columns on a 24 h scale with the now-line. */
function WeekGrid({ ps, from, now, onOpen, status }: { ps: Programme[]; from: number; now: number; onOpen: (b: Block, p: Programme) => void; status?: Map<string, RowStatus> }) {
  const { locale, tz } = useLocale();
  const byId = new Map(ps.map((p) => [p.id!, p]));
  const cols = Array.from({ length: 7 }, (_, i) => {
    const s = dayStart(from + 12 * 3600_000, tz, i);
    return { s, e: dayStart(s + 12 * 3600_000, tz, 1) };
  });
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-surface" data-testid="epg-week">
      <div className="grid min-w-[56rem] grid-cols-[3rem_repeat(7,minmax(0,1fr))]">
        <div />
        {cols.map((c) => (
          <div key={c.s} className="border-s border-border px-2 py-1.5 text-center text-xs font-medium">
            {new Intl.DateTimeFormat(locale, { timeZone: tz, weekday: 'short', day: 'numeric', month: 'numeric' }).format(new Date(c.s))}
          </div>
        ))}
        <div className="relative h-[72rem] border-t border-border">
          {Array.from({ length: 24 }, (_, h) => (
            <span key={h} className="absolute end-1 text-[10px] tabular-nums text-fg-muted ltr-isolate" style={{ top: `${(h / 24) * 100}%` }}>{String(h).padStart(2, '0')}:00</span>
          ))}
        </div>
        {cols.map((c) => {
          const line = nowLine(c.s, c.e, now);
          return (
            <div key={c.s} className="relative h-[72rem] border-s border-t border-border">
              {Array.from({ length: 24 }, (_, h) => <span key={h} className="absolute start-0 end-0 border-t border-border/40" style={{ top: `${(h / 24) * 100}%` }} aria-hidden />)}
              {layoutColumn(ps, c.s, c.e, now).map((b) => (
                <button key={b.id} type="button" onClick={() => onOpen(b, byId.get(b.id)!)} title={`${fmtTime(new Date(b.startMs), { locale, tz })} ${b.title}`}
                  className={cn('absolute start-0.5 end-0.5 overflow-hidden rounded border px-1 text-start text-[11px] leading-tight hover:z-10 hover:shadow-pop focus-visible:outline-2 focus-visible:outline-primary',
                    b.onAir ? 'border-live bg-live/15' : b.source === 'manual' ? 'border-primary/50 bg-primary/10' : 'border-border bg-surface-2',
                    status?.get(b.id) === 'added' && 'border-ok border-dashed', status?.get(b.id) === 'changed' && 'border-warn border-dashed', status?.get(b.id) === 'removed' && 'line-through opacity-60')}
                  style={{ top: `${b.top}%`, height: `${b.height}%` }}>
                  <span className="tabular-nums ltr-isolate text-fg-muted">{fmtTime(new Date(b.startMs), { locale, tz })}</span> <span className="font-medium">{b.title}</span>
                </button>
              ))}
              {line !== null ? <span className="pointer-events-none absolute start-0 end-0 z-20 border-t-2 border-live" style={{ top: `${line}%` }} aria-hidden /> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

type EditState = { id?: string; p?: Programme; start?: number; focus?: FixField };

function ProgrammeDrawer({ ch, state, onClose, readOnly, lockHours, review, draftLayer, issues }: {
  ch: Channel; state: EditState | null; onClose: () => void; readOnly: boolean; lockHours?: number; review?: boolean; draftLayer?: boolean; issues?: EPGIssue[];
}) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { tz } = useLocale();
  const toast = useToast();
  const save = useSaveProgramme(tenant.slug, ch.id!);
  const del = useDeleteProgramme(tenant.slug, ch.id!);
  const stage = useStageOps(tenant.slug, ch.id!);
  const guard = useLockGuard(lockHours);
  const [confirm, setConfirm] = React.useState(false);
  const [blackout, setBlackout] = React.useState(false);
  const [more, setMore] = React.useState(false);
  const canBlackout = can(scopes, 'delivery:write');
  const p = state?.p;
  const base = (p?.meta ?? null) as EPGMeta | null;
  const [f, setF] = React.useState({ title: '', start: '', end: '' });
  const [mf, setMf] = React.useState<MetaForm>(() => metaForm(null));
  React.useEffect(() => {
    if (!state) return;
    const startMs = p ? Date.parse(p.start_at!) : state.start ?? Date.now();
    setF({ title: p?.title ?? '', start: toLocalInput(startMs, tz), end: p?.end_at ? toLocalInput(p.end_at, tz) : p ? '' : toLocalInput(startMs + 30 * 60_000, tz) });
    setMf(metaForm(base));
    setMore(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when a different programme opens
  }, [state?.id, state?.start, tz]);
  // change epg-warnings: opened from a warning → scroll to and focus what fixes it (after the sheet's own autofocus)
  const fixRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const focus = state?.focus;
    if (!focus) return;
    const id = window.setTimeout(() => {
      const el = focus === 'image' || focus === 'rating'
        ? (fixRef.current?.querySelector<HTMLElement>(`[data-testid="epg-fix-${focus}"] button, [data-testid="epg-fix-${focus}"]`) ?? fixRef.current)
        : document.getElementById(focus === 'title' ? 'p-title' : focus === 'start' ? 'p-start' : 'p-end');
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el?.focus({ preventScroll: true });
    }, 120);
    return () => window.clearTimeout(id);
  }, [state?.id, state?.focus]);
  const startIso = fromLocalInput(f.start, tz);
  const endIso = f.end ? fromLocalInput(f.end, tz) : null;
  const invalid = !f.title.trim() ? 'title' : !startIso ? 'start' : endIso && Date.parse(endIso) <= Date.parse(startIso) ? 'end' : null;
  const off = rightsOff(base);
  const startOver = p && ch.playback?.start_over && !off.includes('start_over') && !isExcluded(p) ? ch.playback.start_over.replace('{programme_id}', p.id!) : null;
  const done = (r: SaveResult | void, okTitle: string) => {
    toast.push({ title: r && r.staged ? t`Added to the draft` : okTitle, description: r && r.staged ? t`A publisher publishes the draft.` : undefined, tone: 'ok' });
    onClose();
  };
  const failed = (title: string) => (err: unknown) => toast.push({ title, description: err instanceof ApiError ? (err.problem?.detail ?? err.message) : String(err), tone: 'danger' });
  const onSave = () => {
    if (invalid) return;
    const meta = formMeta(base, mf);
    const draft = { title: f.title.trim(), start_at: startIso!, end_at: endIso, meta: meta as Record<string, unknown> };
    if (draftLayer) {
      const data = { start_at: draft.start_at, title: draft.title, meta, ...(endIso ? { end_at: endIso } : { open_end: true }) };
      stage.mutate([state?.id ? { kind: 'update', programme_id: state.id, data } : { kind: 'create', data }], {
        onSuccess: () => done({ staged: true }, ''), onError: failed(t`Could not save the programme`),
      });
      return;
    }
    void guard.run((confirmLock) => save.mutateAsync({ id: state?.id, draft, confirmLock }).then((r) => done(r, t`Programme saved`)), failed(t`Could not save the programme`));
  };
  const onDelete = () => {
    if (draftLayer) {
      return stage.mutateAsync([{ kind: 'delete', programme_id: state!.id! }]).then(() => { setConfirm(false); done({ staged: true }, ''); }, failed(t`Could not delete the programme`));
    }
    return guard.run((confirmLock) => del.mutateAsync({ id: state!.id!, confirmLock }).then((r) => { setConfirm(false); done(r, t`Programme deleted`); }), failed(t`Could not delete the programme`));
  };
  return (
    <Sheet open={!!state} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent title={state?.id ? t`Programme` : t`Add programme`} closeLabel={t`Close`}>
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); onSave(); }}>
          {p ? <div className="flex items-center gap-2 text-sm"><SourceBadge source={p.source ?? 'epg'} />{p.source === 'epg' ? <span className="text-fg-muted">{t`Imported from the EPG source. Saving an edit keeps it when the guide is re-imported.`}</span> : null}</div> : null}
          {review || draftLayer ? <p className="rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-fg-muted">{t`Changes go to the draft; a publisher publishes them.`}</p> : null}
          {p?.id && issues?.length ? (
            <FixPanel ref={fixRef} ch={ch} p={p} issues={issues} lockHours={lockHours} stage={draftLayer} readOnly={readOnly}
              onApplied={(field, v) => setMf((m) => ({ ...m, [field]: v }))} />
          ) : null}
          <Field id="p-title" label={t`Title`} error={invalid === 'title' && f.title !== '' ? t`Required` : undefined}>
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} disabled={readOnly} required maxLength={300} />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field id="p-start" label={t`Start`}><Input type="datetime-local" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} disabled={readOnly} required className="ltr-isolate" /></Field>
            <Field id="p-end" label={t`End`} hint={t`Empty = until the next programme`} error={invalid === 'end' ? t`End must be after start` : undefined}>
              <Input type="datetime-local" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} disabled={readOnly} className="ltr-isolate" />
            </Field>
          </div>
          <Field id="p-desc" label={t`Description`}>
            <textarea id="p-desc" value={mf.description} onChange={(e) => setMf({ ...mf, description: e.target.value })} disabled={readOnly} rows={4}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-2 focus-visible:outline-primary" />
          </Field>
          <Field id="p-cat" label={t`Category`}><Input value={mf.category} onChange={(e) => setMf({ ...mf, category: e.target.value })} disabled={readOnly} /></Field>
          {mf.image || base?.image ? <img src={mf.image || base?.image} alt="" className="h-24 rounded object-cover" /> : null}
          <Button type="button" variant="link" aria-expanded={more} onClick={() => setMore(!more)}>{more ? t`Fewer details` : t`More details — English, episode, rating, genres, rights`}</Button>
          {more ? <RichMetaFields f={mf} setF={setMf} disabled={readOnly} /> : off.length ? (
            <p className="flex flex-wrap gap-2 text-sm">{off.includes('catchup') ? <Badge tone="warn">{t`No catch-up`}</Badge> : null}{off.includes('start_over') && !off.includes('catchup') ? <Badge tone="warn">{t`No start-over`}</Badge> : null}{off.includes('vod') ? <Badge tone="warn">{t`No VOD`}</Badge> : null}</p>
          ) : null}
          {startOver ? (
            <p className="text-sm"><a className="text-primary underline-offset-4 hover:underline" href={startOver} target="_blank" rel="noreferrer">{t`Start-over URL (HLS)`}</a></p>
          ) : null}
          {p?.id && !draftLayer ? (
            <div className="flex flex-wrap items-center gap-2" data-testid="epg-exclusion"><ExclusionBadge s={exclusionOf(p)} /><ProgrammeExclusionAction programmeId={p.id} s={exclusionOf(p)} /></div>
          ) : null}
          {p?.id && p.end_at && Date.parse(p.end_at) < Date.now() && !draftLayer ? <SubtitleLanguages subjectId={p.id} /> : null}
          {p?.id && p.end_at && Date.parse(p.end_at) < Date.now() && !draftLayer ? <ContentSummaryPanel kind="programme" id={p.id} /> : null}
          {p?.id && p.end_at && Date.parse(p.end_at) < Date.now() && !draftLayer ? <ArticleRequestPanel id={p.id} /> : null}
          {p?.id && p.end_at && Date.parse(p.end_at) < Date.now() && !draftLayer ? <VerticalClipsPanel programmeId={p.id} /> : null}
          {p && canBlackout && !draftLayer ? (
            <p><Button type="button" variant="secondary" size="sm" onClick={() => setBlackout(true)}><Ban className="size-4" aria-hidden /> {t`Blackout…`}</Button></p>
          ) : null}
          {!readOnly ? (
            <div className="flex items-center justify-between gap-2 pt-2">
              {state?.id ? <Button variant="danger" onClick={() => setConfirm(true)}><Trash2 className="size-4" aria-hidden /> {t`Delete`}</Button> : <span />}
              <Button type="submit" loading={save.isPending || stage.isPending} disabled={!!invalid}>{t`Save`}</Button>
            </div>
          ) : null}
        </form>
        {state?.id ? (
          <ConfirmByNameDialog open={confirm} onOpenChange={setConfirm} name={p?.title ?? ''} title={t`Delete this programme?`}
            description={p?.source === 'epg' ? t`It comes from the EPG source and will return at the next import unless the source drops it.` : undefined}
            labels={{ field: t`Type the programme title to confirm`, confirm: t`Delete`, cancel: t`Cancel`, close: t`Close` }} busy={del.isPending || stage.isPending}
            onConfirm={async () => { await onDelete(); }} />
        ) : null}
        {p && !draftLayer ? (
          <BlackoutDialog channelId={ch.id!} cdnHostname={tenant.cdn_hostname} open={blackout} onOpenChange={setBlackout}
            seed={{ programmeId: p.id, title: p.title ?? undefined, start: p.start_at, end: p.end_at }} />
        ) : null}
        {guard.dialog}
      </SheetContent>
    </Sheet>
  );
}

// --- Import ------------------------------------------------------------------------------------------------------

function ResultLine({ r }: { r: EpgImportResult }) {
  const { t } = useLingui();
  return (
    <div className="space-y-1 text-sm" data-testid="epg-result">
      <p className="num">{t`Created ${r.created}, updated ${r.updated}, removed ${r.deleted}, other channels skipped ${r.skipped}`}</p>
      {r.errors.length > 0 ? <ul className="list-disc ps-5 text-danger">{r.errors.slice(0, 8).map((e) => <li key={e} className="ltr-isolate">{e}</li>)}</ul> : null}
    </div>
  );
}

function ImportPanel({ ch }: { ch: Channel }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale, tz } = useLocale();
  const toast = useToast();
  const canWrite = can(scopes, 'channels:write');
  const q = useChannelEpg(tenant.slug, ch.id);
  const upload = useUploadXmltv(tenant.slug, ch.id!);
  const saveCfg = useSaveEpg(tenant.slug, ch.id!);
  const now = useImportNow(tenant.slug, ch.id!);
  const [file, setFile] = React.useState<File | null>(null);
  const [xmltvCh, setXmltvCh] = React.useState('');
  const [lastUpload, setLastUpload] = React.useState<EpgImportResult | null>(null);
  const [cfg, setCfg] = React.useState<EpgConfig | null>(null);
  React.useEffect(() => { if (q.data) setCfg(q.data.config); }, [q.data]);
  const errs = cfg ? validateConfig(cfg) : {};
  const errText = (k: 'url' | 'required' | 'range' | undefined) => (k === 'url' ? t`Enter an http(s) URL` : k === 'required' ? t`Required when enabled` : k === 'range' ? t`Out of range` : undefined);
  const problem = (err: unknown) => (err instanceof ApiError ? (err.problem?.detail ?? err.message) : String(err));
  const st = q.data?.status;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section className="space-y-3 rounded-lg border border-border bg-surface p-4" aria-labelledby="epg-upload-h">
        <h2 id="epg-upload-h" className="font-semibold">{t`Upload an XMLTV file`}</h2>
        <p className="text-sm text-fg-muted">{t`Programmes of the chosen XMLTV channel replace the imported programmes in the file's time window. Manual programmes are kept.`}</p>
        <Field id="xmltv-file" label={t`XMLTV file (.xml, up to 10 MB)`}>
          <Input type="file" accept=".xml,application/xml,text/xml" disabled={!canWrite} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </Field>
        <Field id="xmltv-ch" label={t`XMLTV channel id`} hint={t`Only needed when the file has several channels`}>
          <Input value={xmltvCh} onChange={(e) => setXmltvCh(e.target.value)} disabled={!canWrite} className="ltr-isolate" placeholder="tv10" />
        </Field>
        <Button disabled={!canWrite || !file} loading={upload.isPending} onClick={() => file && upload.mutate({ file, xmltvChannel: xmltvCh || undefined }, {
          onSuccess: (r) => { setLastUpload(r); toast.push({ title: t`Guide imported`, tone: 'ok' }); },
          onError: (err) => toast.push({ title: t`Import failed`, description: problem(err), tone: 'danger' }),
        })}><Upload className="size-4" aria-hidden /> {t`Import file`}</Button>
        {lastUpload ? <ResultLine r={lastUpload} /> : null}
      </section>

      <section className="space-y-3 rounded-lg border border-border bg-surface p-4" aria-labelledby="epg-sched-h">
        <h2 id="epg-sched-h" className="font-semibold">{t`Scheduled source`}</h2>
        {q.isPending || !cfg ? <Skeleton className="h-48 w-full" /> : q.isError ? <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load EPG settings`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} /> : (
          <form className="space-y-3" onSubmit={(e) => {
            e.preventDefault();
            if (Object.keys(errs).length) return;
            saveCfg.mutate(cfg, { onSuccess: () => toast.push({ title: t`EPG settings saved`, tone: 'ok' }), onError: (err) => toast.push({ title: t`Could not save EPG settings`, description: problem(err), tone: 'danger' }) });
          }}>
            <label className="flex items-center gap-3 text-sm"><Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} disabled={!canWrite} aria-label={t`Import automatically`} /> {t`Import automatically`}</label>
            <Field id="epg-format" label={t`Format`}>
              <Select ariaLabel={t`Format`} value={cfg.format} onValueChange={(v) => setCfg({ ...cfg, format: v as EpgConfig['format'] })}
                options={[{ value: 'xmltv', label: t`XMLTV URL` }, { value: 'redge_json', label: t`Redge portal (tv10, 14)` }]} />
            </Field>
            <Field id="epg-url" label={t`Source URL`} error={errText(errs.source_url)}>
              <Input value={cfg.source_url} onChange={(e) => setCfg({ ...cfg, source_url: e.target.value })} disabled={!canWrite} className="ltr-isolate" inputMode="url" />
            </Field>
            {cfg.format === 'redge_json' && canWrite ? (
              <div className="flex flex-wrap gap-2 text-xs">{REDGE_PRESETS.map((pr) => <Button key={pr.url} size="sm" variant="ghost" onClick={() => setCfg({ ...cfg, source_url: pr.url })}>{pr.label}</Button>)}</div>
            ) : null}
            {cfg.format === 'xmltv' ? (
              <Field id="epg-xch" label={t`XMLTV channel id`}><Input value={cfg.xmltv_channel_id ?? ''} onChange={(e) => setCfg({ ...cfg, xmltv_channel_id: e.target.value })} disabled={!canWrite} className="ltr-isolate" /></Field>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <Field id="epg-int" label={t`Every (minutes)`} error={errText(errs.interval_min)}><Input type="number" min={5} max={1440} value={cfg.interval_min} onChange={(e) => setCfg({ ...cfg, interval_min: Number(e.target.value) })} disabled={!canWrite} /></Field>
              <Field id="epg-days" label={t`Days ahead`} error={errText(errs.days_ahead)}><Input type="number" min={1} max={14} value={cfg.days_ahead} onChange={(e) => setCfg({ ...cfg, days_ahead: Number(e.target.value) })} disabled={!canWrite} /></Field>
            </div>
            {canWrite ? (
              <div className="flex flex-wrap gap-2">
                <Button type="submit" loading={saveCfg.isPending} disabled={Object.keys(errs).length > 0}>{t`Save`}</Button>
                <Button variant="secondary" loading={now.isPending} disabled={!q.data?.config.source_url} onClick={() => now.mutate(undefined, {
                  onSuccess: (r) => toast.push({ title: t`Guide imported`, description: t`Created ${r.created}, updated ${r.updated}, removed ${r.deleted}`, tone: 'ok' }),
                  onError: (err) => toast.push({ title: t`Import failed`, description: problem(err), tone: 'danger' }),
                })}><RefreshCw className="size-4" aria-hidden /> {t`Import now`}</Button>
              </div>
            ) : null}
          </form>
        )}
        {st?.last_run_at ? (
          <div className="space-y-1 border-t border-border pt-3 text-sm" data-testid="epg-status">
            <p>{t`Last run`}: <span className="num">{fmtDateTime(st.last_run_at, { locale, tz })}</span> {st.error ? <Badge tone="danger">{t`Failed`}</Badge> : <Badge tone="ok">{t`OK`}</Badge>}</p>
            {st.last_ok_at ? <p className="text-fg-muted">{t`Last success`}: <span className="num">{fmtDateTime(st.last_ok_at, { locale, tz })}</span></p> : null}
            {st.result ? <ResultLine r={st.result} /> : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}

// --- Export ------------------------------------------------------------------------------------------------------

function LinkRow({ label, value, testId }: { label: string; value: string; testId: string }) {
  const { t } = useLingui();
  const toast = useToast();
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium">{label}</p>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded bg-surface-2 px-2 py-1 text-xs ltr-isolate" title={value} data-testid={testId}>{value}</code>
        <Button size="icon" variant="ghost" aria-label={t`Copy ${label}`} onClick={() => { void navigator.clipboard?.writeText(value); toast.push({ title: t`Copied`, tone: 'ok' }); }}><Copy className="size-4" aria-hidden /></Button>
        <a className="text-sm text-primary underline-offset-4 hover:underline" href={value} target="_blank" rel="noreferrer">{t`Open`}</a>
      </div>
    </div>
  );
}

function ExportPanel({ ch }: { ch: Channel }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const q = useChannelEpg(tenant.slug, ch.id);
  if (q.isPending) return <Skeleton className="h-40 w-full" />;
  if (q.isError) return <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load EPG settings`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  const x = q.data.export;
  return (
    <section className="max-w-3xl space-y-4 rounded-lg border border-border bg-surface p-4">
      <p className="text-sm text-fg-muted">{t`Public links to this guide for apps, smart-TV guides and partners. They update within five minutes of a change and cover the recording window back and 14 days ahead.`}</p>
      <LinkRow label={t`XMLTV — this channel`} value={x.xmltv} testId="epg-link-xmltv" />
      <LinkRow label={t`JSON — this channel (with start-over URLs)`} value={x.json} testId="epg-link-json" />
      <LinkRow label={t`XMLTV — all channels of ${tenant.name}`} value={x.tenant_xmltv} testId="epg-link-tenant" />
      <p className="text-xs text-fg-muted">{t`Add ?past_days=N (0–14) to a link for a longer backwards guide. Approved AI guide entries are included per language.`}</p>
      <p className="text-xs text-fg-muted">
        <Link to={'/t/$slug/live/$channelId' as never} params={{ slug: tenant.slug, channelId: ch.id } as never} search={{ tab: 'catchup' } as never} className="text-primary underline-offset-4 hover:underline">{t`See the guide on the catch-up timeline`}</Link>
      </p>
    </section>
  );
}
